import React, { useState } from "react";
import { motion } from "framer-motion";
import { ArrowUpRight, CheckCircle2 } from "lucide-react";
import { Link } from "react-router-dom";

const cardData = [
  {
    title: "For Educational institution",
    subtitle: "Integrate AI into your curriculum with hands-on labs, electives, and applied learning across disciplines.",
    bullets: [
      "Introduce AI electives across departments and degrees",
      "Enable lab-based learning without heavy infrastructure",
      "Train and support faculty to learn and teach AI",
      "Get access to AI Tools for Curriculum Development, Proctoring, Assessments and more"
    ],
    bgImage: "https://customer-assets.emergentagent.com/job_laughing-sinoussi-3/artifacts/lpa6qzgm_f5f27e8b59ca3fc940b79e2c80b0377bfdcbce6a.png",
    linkTo: "/educational-institution"
  },
  {
    title: "For Enterprises",
    subtitle: "Upskill teams with practical AI programs designed for real workflows and business impact.",
    bullets: [
      "Run role-specific AI training programs",
      "AI Literacy for leadership and execution teams",
      "Enable true AI adoption across the organisation",
      "Customised LnD Solutions for AI Skilling and Adoption"
    ],
    bgImage: "https://customer-assets.emergentagent.com/job_laughing-sinoussi-3/artifacts/0z5h3up1_8c4f726e484fd213ff6c65f6b0acf3a5756e2a7f.png",
    linkTo: "/for-enterprises"
  }
];

const BuiltForFuture = () => {
  const [hoveredCard, setHoveredCard] = useState(0); // Default: first card (Universities) expanded

  return (
    <section className="py-16 md:py-28 bg-white" style={{ fontFamily: "'DM Sans', sans-serif" }}>
      <div className="max-w-[1300px] mx-auto px-6 md:px-12 lg:px-24">
        {/* Heading - Exactly matching Figma */}
        <motion.h2
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, amount: 0.4 }}
          transition={{ duration: 0.6, ease: "easeOut" }}
          className="text-center text-[#1a1a2e] mb-4 leading-[1.1]"
          style={{ fontSize: "clamp(30px, 5vw, 56px)", fontWeight: 800 }}
        >
          Built for Future<br />of Learning
        </motion.h2>
        <motion.p
          initial={{ opacity: 0, y: 16 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, amount: 0.4 }}
          transition={{ duration: 0.6, delay: 0.1, ease: "easeOut" }}
          className="text-center text-gray-500 text-[15px] sm:text-[16px] md:text-[17px] max-w-[680px] mx-auto mb-12 md:mb-14 leading-relaxed px-2"
        >
          Designed to enable practical AI skilling across academic institutions and modern enterprises.
        </motion.p>

        {/* Cards */}
        <div className="flex flex-col lg:flex-row gap-6 max-w-[1200px] mx-auto lg:h-[600px]">
          {cardData.map((card, index) => {
            const isExpanded = hoveredCard === index;

            return (
              <div
                key={index}
                onMouseEnter={() => setHoveredCard(index)}
                className={`relative rounded-2xl overflow-hidden cursor-pointer transition-all duration-700 ease-out min-h-[480px] lg:min-h-0 ${
                  isExpanded ? 'lg:flex-[1.2]' : 'lg:flex-[0.8]'
                }`}
              >
                {/* Background Image */}
                <div 
                  className="absolute inset-0 bg-cover bg-center"
                  style={{ backgroundImage: `url(${card.bgImage})` }}
                />
                {/* Dark Linear Gradient Overlay - MUCH darker to match Figma */}
                <div className="absolute inset-0 bg-gradient-to-t from-black/95 via-black/70 to-black/40" />

                {/* Content */}
                <div className="relative z-10 h-full flex flex-col justify-between p-7 sm:p-8 md:p-10">
                  {/* Top: Title */}
                  <h3 className="text-[28px] sm:text-[32px] lg:text-[42px] font-bold text-white leading-[1.1]">
                    {card.title}
                  </h3>

                  {/* Bottom: Subtitle, Button, Bullets */}
                  <div>
                    {/* Subtitle (always visible) */}
                    <p className="text-[15px] sm:text-[16px] lg:text-[17px] text-white/90 leading-relaxed mb-5 lg:mb-6">
                      {card.subtitle}
                    </p>

                    {/* Know more button (always visible) */}
                    <Link
                      to={card.linkTo}
                      className="inline-flex items-center gap-2 px-5 py-2.5 lg:px-6 lg:py-3 bg-white text-[#1a1a2e] text-[14px] lg:text-[15px] font-semibold rounded-full mb-6 lg:mb-8 hover:bg-white/95 transition-all"
                      data-testid={`know-more-${index === 0 ? 'education' : 'enterprise'}`}
                    >
                      Know more
                      <ArrowUpRight className="w-4 h-4" />
                    </Link>

                    {/* Bullet Points - Always visible on mobile/tablet, expand on hover for desktop */}
                    <div
                      className={`space-y-3 lg:space-y-4 overflow-hidden lg:transition-all lg:duration-700 ${
                        isExpanded ? 'lg:opacity-100 lg:max-h-96' : 'lg:opacity-0 lg:max-h-0'
                      }`}
                    >
                      {card.bullets.map((bullet, bi) => (
                        <div key={bi} className="flex items-start gap-3">
                          <CheckCircle2 className="w-5 h-5 text-emerald-400 mt-0.5 flex-shrink-0" />
                          <span className="text-[14px] lg:text-[16px] text-white leading-relaxed">
                            {bullet}
                          </span>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
};

export default BuiltForFuture;
